<template>
    <div class="subscribePart">
        <slot name="legacyTips"></slot>
        <duration-tab
            v-if="durationList.length>0"
            :durationList="durationList"
            :activeDurationIndex="currentDeviceGroup"
            :yearPlansMaxDiscount="yearPlansMaxDiscount"
            align="center"
            @change="handleDurationChange"
        />
        <available-countries-dialog
            v-if="showAvailableCountries"
            :supported-country-list="supportedCountryList"
            @confirm='showSubscription = true'
            @cancel="showAvailableCountries = false"
            :storageLocList="storageLocList"
            :cloudProductLists="cloudProductLists"
        />
        <email-subscribe-dialog
            v-if="showSubscription"
            class='email-subscribe'
            @cancel="showSubscription = false"
        />
    </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { config } from 'CONFIG';
import { isInVisibleArea, valueWithFallbacks } from '@/common/js/utils';
import AvailableCountriesDialog from '@/components/Dialogs/AvailableCountriesDialog.vue';
import EmailSubscribeDialog from '@/components/Dialogs/EmailSubscribeDialog.vue';
import { IDuration, IPlan, IPlanSeries, ISelectorStyle } from '@reolink-cloud/interface';
import IconCountry from '@/common/images/common/combo/co_web.svg';
import { countriesManager } from '@/lib';
import { IDeviceGroupWithPlans } from '@/view/reolink/homepage/lib/types';
import * as _ from '@reolink-web/utils';
import { mapGetters } from 'vuex';
import DurationTab from '@/components/DurationTab.vue';
import reolinkGa from '@/common/js/gtagPageview';

interface ISeriesItem extends IPlanSeries {
    plans: IPlan[];
}

export default Vue.extend({
    components: {
        AvailableCountriesDialog,
        EmailSubscribeDialog,
        DurationTab
    },
    props: {
        storageLocList: Object,
        cloudProductLists: Object,
        switchingLegacy: {
            type: Boolean,
            default: false
        },
        hideCountryDialog: Boolean,
        durationList: Array as PropType<IDuration[]>
    },
    data () {

        return {
            config,
            deviceList: [] as any[],
            showSubscription: false,
            showAvailableCountries: false,
            isEurope: false,
            locations: [] as any[],
            locationIndex: 0,
            supportedCountryList: [] as any[],
            deviceGroupDes: '',
            currentDeviceGroup: 0,
            supportedDeviceList: [] as any[],
            locationKey: this.$route.query.loc,
            modelKey: this.$route.query.model || '',
            dialogStack: [] as any[],
            planNoticeText: '',
            showAvailableDevices: false,
            isSubPage: false,
            isNewSubPage: false,
            defaultSelected: '',
            scrollHandleEnable: false,
            mobileSubBtnShow: false,
            IconCountry
        };

    },
    created () {

        this.isSubPage = window.location.href.includes( 'plan' );
        this.isNewSubPage = window.location.href.includes( 'subscribe-plan' );
        const HIDE_COUNTRY_KEY = 'hideCloudCountryDialog';
        if ( !this.isStorageAvailableCountry && !sessionStorage.getItem( HIDE_COUNTRY_KEY )) {

            this.showAvailableCountries = true;
            sessionStorage.setItem( HIDE_COUNTRY_KEY, 'true' );

        }

    },
    computed: {
        ...mapGetters( ['country', 'isStorageAvailableCountry'] as const ),
        isShowTitle (): boolean {

            return ( this.isMobile || window.location.href.includes( 'subscribe' )) && !window.location.href.includes( 'update' );

        },
        userStatus (): string {

            return this.$store.state.user.status;

        },
        selectorStyle (): ISelectorStyle {

            return !this.isMobile ? {
                widthOut: 3,
                heightOut: 0.48,
                widthIn: 3,
                heightIn: 0.48,
                selectMarginLeft: 0.16,
                triangleRight: 0.16,
                maxHeight: 3.08,
                fontSizeIn: 0.14,
                fontSizeOut: 0.16
            } : {
                widthOut: -1,
                heightOut: 0.48,
                widthIn: -1,
                heightIn: 0.4,
                selectMarginLeft: 0.16,
                triangleRight: 0.16,
                maxHeight: 4.8,
                fontSizeIn: 0.16,
                fontSizeOut: 0.14
            };

        },
        defaultStorageRegion (): Record<string, any> {

            return this.cloudProductLists.defaultStorageRegion;

        },
        isMobile(): boolean {

            return this.$store.getters.isMobile;

        },
        defaultCountry(): string {

            return countriesManager.getSingleCountryInfo(
                valueWithFallbacks( this.country, this.cloudProductLists.country, config.fallbackCountry )
            ).name;

        },
        yearPlansMaxDiscount(): string | undefined {

            const planSeriesList: ISeriesItem[] = Object.values( this.storageLocList[this.locations[0].value]?.planSeries ?? {});
            const maxDiscount = Math.max( ...planSeriesList.map(( series: ISeriesItem ) => Math.max( ...series.plans.filter(( plan: IPlan ) => plan.duration.unit === 'year' )
                .map(( plan: IPlan ) => {

                    const regularPrice = parseFloat( plan.pricing.regularPrice );
                    if ( regularPrice === 0 ) {

                        return 0;

                    } else {

                        return 100 - 100 * parseFloat( plan.pricing.salePrice ) / parseFloat( plan.pricing.regularPrice );

                    }

                }))),
            0 );
            return maxDiscount >= 1 ? this.$t( 'plan.discount_up_to', {
                discount: maxDiscount.toFixed( 0 )
            }).toString() : undefined;

        }
    },
    methods: {

        scrollHandle ( ): false | undefined {

            if ( !this.scrollHandleEnable ) {

                return false;

            }
            this.scrollHandleEnable = false;
            setTimeout(() => {

                if ( $( 'body' ).eq( 0 )[ 0 ].offsetWidth < 1200 ) {

                    this.calcPlanIsInWindow( );

                }
                this.scrollHandleEnable = true;

            }, 100 );

        },
        calcPlanIsInWindow ( ): void {

            let visible = isInVisibleArea( '#plans-select' );
            this.mobileSubBtnShow = visible;

        },
        // 机型组切换
        handleDurationChange ( val: number ): void {

            reolinkGa.sendSimpleEvent( `Cloud Storage Service-${this.durationList[val].key}` );
            this.currentDeviceGroup = val;
            this.$emit( 'changeDuration', this.durationList[val] );

        },
        // 存储地切换
        handleLocationChange ( val ): void {

            this.supportedCountryList = _.Array.unique( Object.values<any>( this.storageLocList ).map( loc => loc.supportedCountryList ).flat(), item => item.code );
            let list: any[] = [];
            for ( const seriesItem of ( Object.values( this.storageLocList[val]?.planSeries ?? {})) as ISeriesItem[] ) {

                for ( const planItem of seriesItem.plans ) {

                    const amount = planItem.duration.amount;
                    const unit = planItem.duration.unit;
                    const key = `${amount}-${unit}`;
                    if ( !list.some( item => item.key === key ) && !planItem.seriesKey.includes( 'basic' )) {

                        list.push({
                            key,
                            title: this.$tc( `cycle.${unit}`, amount === 1 ? 1 : 0, { count: amount }),
                            id: key,
                            amount,
                            unit
                        });

                    }

                }

            }
            const unitToOrder = {
                year: 0,
                month: 3,
                week:2,
                day: 1
            };
            list = list.sort(( a, b ) => {

                if ( a.unit === b.unit ) {

                    return a.amount - b.amount;

                } else {

                    return unitToOrder[a.unit] - unitToOrder[b.unit];

                }

            });
            this.defaultSelected = val;

            const basicSerials = ( Object.values( this.storageLocList[val].deviceGroupsList ?? {}) as IDeviceGroupWithPlans[] ).map( item => item.basicSerial ).filter( item => item !== undefined );
            this.$emit( 'changeSelect', { list: list, locationKey: val, basicSerial: basicSerials[0], supportedDeviceList: this.supportedDeviceList, supportedCountryList: this.supportedCountryList });
            if ( this.isShowTitle ) {

                this.planNoticeText = typeof this.cloudProductLists.storageRegions[val].i18n.note !== 'undefined' ? this.cloudProductLists.storageRegions[val].i18n.note : '';

            }

        }

    },
    watch: {
        storageLocList: {
            handler ( newVal ) {

                if ( typeof newVal === 'undefined' || JSON.stringify( newVal ) === '{}' ) {

                    return;

                }
                let regionsData = this.cloudProductLists.storageRegions;
                Object.keys( newVal ).map(( loc ) => {

                    if ( loc === this.defaultStorageRegion.loc ) {

                        return;

                    }
                    this.locations.push({
                        'label': regionsData[loc].i18n.name,
                        'value': regionsData[loc].key,
                        'key': regionsData[loc].key,
                        id: regionsData[loc].key,
                        'sortOrder': regionsData[loc].sortOrder
                    });

                });
                this.locations = this.locations.sort(( a, b ) => a.sortOrder - b.sortOrder );
                if ( JSON.stringify( this.defaultStorageRegion ) !== '{}' ) {

                    this.locations.unshift({
                        'label': this.defaultStorageRegion.i18n.name,
                        'value': this.defaultStorageRegion.loc,
                        'key': this.defaultStorageRegion.loc,
                        'sortOrder': this.defaultStorageRegion.sortOrder,
                        id: this.defaultStorageRegion.loc
                    });

                }

                this.defaultSelected = this.locations[0].value;
                this.handleLocationChange( this.locations[0].value );

                if ( window.location.href.includes( '#showAvailableCountries' )) {

                    document.getElementsByName( 'subscribe' )[0].scrollIntoView();
                    this.showAvailableCountries = true;

                }

            },
            immediate: true
        },
        supportedCountryList ( val ): void {

            this.supportedCountryList = val;

        }
    }
});
</script>

<style scoped rel="stylesheet/css" lang="less">

    ::-webkit-scrollbar{
        width: 4px;
        height: 4px;
    }
    ::-webkit-scrollbar-thumb{
        background-color: #CCCCCC;
        border-radius: 8px;
    }
    .subscribePart{
        display: flex;
        justify-content: center;
        align-items: center;
        .title{
            background: #F7F8F9;
            @media @mobile{
                background-color: #fff;
                text-align: left;
                padding: 0 20px;
                padding-bottom: .3rem;
            }
        }
        .notice-for-free{
            background: #F7F8F9;
            font-weight: 400;
            padding: 0 20px .32rem;
            color:red;
            @media @mobile{
                font-size: .26rem;
                line-height: 1.4;
                background: #fff;
                margin:0;
            }
            & /deep/ .title{
                @media @mobile{
                    font-size: .28rem !important;
                    line-height: 1.4;
                    text-align: left;
                    margin: 0;
                    padding: 0;
                }
            }

        }
        .selectBox{
            @media @pc{
                margin-bottom: 0.12rem;
            }
            @media @mobile{
                padding:0 20px;
                background-color: #F7F8F9;
            }

            .loc-select{
                @media @mobile{
                    margin-bottom: .2rem;
                }
            }
        }
        .selectText{
            font-weight: 500;
            color: #333;
            line-height: 1.4;
            @media @pc{
                font-size: .3rem;
                margin-right: .2rem;
                margin-bottom: .3rem;
            }
            @media @mobile{
                font-size: .36rem;
                margin-bottom: .3rem;
                line-height: 1.2;
            }
        }
        .planChoose{
            @media @mobile{
                background-color:#f7f8f9;
                .title{
                    background-color:#f7f8f9;
                    font-weight: 500;
                    color: #333;
                    font-size: .36rem;
                    line-height: 1.2;
                }
                .notice-for-free{
                    background-color:#f7f8f9;
                    .title{
                        background-color:#f7f8f9;
                    }
                }

            }
        }
        .countryBtnBox{
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 4px;
            @media @pc{
                &.switchingLegacy{
                    margin-bottom: 0rem;
                }
            }
            .mobile({
                flex-wrap: wrap;
            });
            .country-text{
                font-size: 16px;
                font-weight: 500;
                line-height: 24px;
                align-items: center;
                gap: 4px;
                min-width: 0;
                img{
                    vertical-align: text-bottom;
                }
                .mobile({
                    margin-bottom: 4px;
                });
            }

            .countryBtn{
                font-size: 16px;
                color:@theme;
                line-height: 24px;
                font-weight: 500;
                cursor: pointer;
                flex-shrink: 0;
                span{
                    transition: all .2s linear;
                    margin-left: 8px;
                }
            }
        }

        .content-container{
            padding: .4rem .6rem 0;
            @media @mobile{
                padding: .5rem 15px 0;

            }
            .countryBtn{
                @media @mobile{
                    display: block;
                    background-color: #fff;
                    margin-bottom: 0;
                }

                font-size: .16rem;
                color:@theme;
                line-height: 1.2;
                font-weight: 500;
                cursor: pointer;
                span{
                    transition: all .2s linear;
                    margin-left: 8px;
                }
                &:hover span{
                    margin-left: 16px;
                }
                @media @mobile{
                    font-size:.28rem;
                }
            }
        }
        .content{
            padding:.2rem .4rem;
            background-color:#fff;
            border-radius: 16px;
            @media @mobile{
                padding: 0;
                background: #FFF;
            }
            .listTitle{
                padding-bottom: .16rem;
                font-size: .16rem;
                line-height: 1.2;
                color: #333333;
                @media @mobile{
                    padding-bottom: .20rem;
                    font-size: .32rem;
                }
            }
            .description{
                @media @mobile{
                    span,p{
                        font-size: .28rem;
                        margin:0;
                    }
                    p.countryBtn{
                        margin-top: .2rem;
                        display: inline-block;
                    }
                }
                @media @pc{
                    span,p{
                        font-size: .16rem;
                        margin:0;
                    }
                    p.countryBtn{
                        margin-top: .1rem;
                        display: inline-block;
                    }
                }

            }
            /deep/ .item-list{

                border: 1px solid #CCCCCC;
                border-radius: 16px;
                padding: .1rem;
                padding-right: 0;
                max-height: 1.4rem;
                background:#F7F8F9;
                overflow-y: hidden;
                @media @mobile{
                    max-height: 3.2rem;
                    background:#fff;
                }
                &::-webkit-scrollbar{
                    width:8px;
                }
                &::-webkit-scrollbar-thumb{
                    background-color: #CCCCCC;
                    border-radius: 8px;
                }
                ul{
                    padding: 0;
                    max-height: 1.4rem;
                    overflow-y: auto;
                    overflow-y: overlay;
                    @media @mobile{
                        overflow-y: auto;
                        overflow-y: overlay;
                        max-height: 3.2rem;
                    }
                    li{
                        margin-top: 0;
                        width:20%;
                        &.no-match{
                            width: 100%;
                        }
                        @media @mobile{
                            width:50%;
                            padding: .1rem;
                        }
                        img{
                            width: .5rem;
                            margin-right: .08rem;
                            @media @mobile{
                                width: .8rem;
                                margin-right: .1rem;
                            }
                        }
                        span{
                            font-size: .14rem;
                            line-height: 1.2;
                            @media @mobile{
                                font-size: .26rem;
                            }
                        }
                    }
                }
            }

        }

        /deep/ .el-input__inner{
            background-color: @bg_03 !important;
        }
    }
    /*清除ie的默认选择框样式清除，隐藏下拉箭头*/
    .subscribePart select::-ms-expand { display: none; }

    .subscribePart input::-webkit-input-placeholder,
    .subscribePart input::-moz-placeholder {
        color: #ccc;
    }

    .smallMargin{
        @media @pc{
            margin-bottom: .16rem !important;
        }
    }
</style>
